import React, { useEffect, useState, useRef } from "react";
import { PropositionBarDataContainer } from "./types/PropoisitionBarData";
import { Client, Stage } from "./types/Utils";
import { carouselSettings, settings } from "./utils/Settings";
import parse from "html-react-parser";
import { getSiteUrl } from './helpers'
import classNames from "classnames/bind";

interface RequestInfo {
  client: Client;
  stage: Stage;
}

function App(props: RequestInfo) {
  const client: Client = props.client || Client.AOL;
  const environment: Stage = props.stage || Stage.Live;

  const propositionBarDataContainer: PropositionBarDataContainer =
    settings[client] || settings["AOL"];
    
  const [mobileView, setMobileView] = useState(true);
  const [currentWidth, setCurrentWidth] = useState(0);
  const propositionItemsContainer = useRef<HTMLDivElement>(null);

  let timeouts: ReturnType<typeof setTimeout>[] = [];
  let interval: ReturnType<typeof setInterval>;

  useEffect(() => {
    handleResize();
    handleVisibilityChange();
    window.addEventListener("resize", handleResize);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
      false
    );
  }, []);

  // Handle page visibility change events
  function handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      initialise();
    } else {
      initialise(true);
    }
  }

  function initialise(restart: boolean = false) {
    const width = window.innerWidth;
    setCurrentWidth(width);
    if (width < propositionBarDataContainer.fullBp) {
      //Carousel
      setMobileView(true);
      reset(true);
      if (restart) {
        startAnimation();
      }
    } else {
      //Full
      reset();
      setMobileView(false);
    }
  }

  function startAnimation() {
    animate();
    interval = setInterval(animate, carouselSettings.interval + 100)
  }

  function animate(){
    const propositionItems = propositionItemsContainer.current;
    if(propositionItems){
      const items = propositionItems.children;
      const currentItem = items[carouselSettings.index];
      currentItem.classList.remove("hidden");
      const timeout = setTimeout(() => {
        // show item
        currentItem.classList.add("opacity-100");
        // store index of next item
        carouselSettings.index = getNextCarouselItemIndex(items);
        // start timer for showing state till we need to fade out
        const timeout = setTimeout(() => {
          // start fade out
          const previousItem = items[getPreviousCarouselItemIndex(items)];
          previousItem.classList.remove("opacity-100");
  
          const timeout = setTimeout(() => {
            // hide from dom
            previousItem.classList.add("hidden");
            // repeat
          }, carouselSettings.transition);
  
          timeouts.push(timeout);
        }, carouselSettings.delay);
  
        timeouts.push(timeout);
      }, carouselSettings.transition);
  
      timeouts.push(timeout);
    }
  }

  const handleResize = () => {
    const width = window.innerWidth;
    setCurrentWidth(width);
    if (width < propositionBarDataContainer.fullBp) {
      //Carousel
      initialise(true);
    } else {
      //Full
      initialise();
    }
  };

  function reset(mobile: boolean = false) {
    carouselSettings.index = 0;
    timeouts.forEach((t) => {
      clearTimeout(t);
    });
    timeouts = [];
    clearInterval(interval);

    const propositionItems = propositionItemsContainer.current;

    if (propositionItems) {
      const items = propositionItems.children;
      Array.from(items).forEach((el) => {
        if (el.classList.contains("hidden")) {
          el.classList.remove("hidden");
        }
      });

      if (mobile) {
        Array.from(items).forEach((el) => {
          if (!el.classList.contains("hidden")) {
            el.classList.add("hidden");
          }
          if (el.classList.contains("opacity-100")) {
            el.classList.remove("opacity-100");
          }
        });
      }
    }
  }

  function getNextCarouselItemIndex(items: HTMLCollection) {
    let nextIndex: number = 0;
    if (carouselSettings.index < items.length - 1) {
      nextIndex = carouselSettings.index + 1;
    } else {
      nextIndex = 0;
    }
    return nextIndex;
  }

  function getPreviousCarouselItemIndex(items: HTMLCollection) {
    let previousIndex: number = 0;
    if (carouselSettings.index === 0) {
      previousIndex = items.length - 1;
    } else {
      previousIndex = carouselSettings.index - 1;
    }
    return previousIndex;
  }

  const transition = {
    transition: `opacity ${carouselSettings.transition}ms`,
  };
  const data = propositionBarDataContainer.data;

  return (
    <div className="w-full bg-gray-10 py-3">
      <div
        className="max-w-screen-xl mx-auto flex h-6 justify-center xl:justify-between"
        ref={propositionItemsContainer}
      >
        {data.map(
          (
            {
              icon,
              img,
              text,
              link,
              dataTag,
              dataProposition,
              linkNewTab,
              dataTestId,
            },
            i
          ) => {

            const url = link.indexOf('https://') === -1 ? getSiteUrl(client, environment) + link : link;

            return (
              <a
                href={url}
                target={linkNewTab ? "_blank" : "_self"}
                key={`proposition-${i}`}
                className="flex item-1 opacity-0 xl:opacity-100 no-underline text-body-sm items-center justify-center xl:justify-between"
                data-tag={dataTag ? dataTag : null}
                data-testid={dataTestId}
                data-proposition={dataProposition ? dataProposition : null}
                style={transition}
              >
                {icon && <i className={`ico ${icon} ico-2x mr-2`}></i>}
                {img && <img src={img} className="mr-2" data-testid="image" alt="" />}
                <span className="leading-tight font-regular">{parse(text)}</span>
              </a>
            );
          }
        )}
      </div>
    </div>
  );
};

export default App;
